import { Onboarding, Statements } from "@capitalos/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Building,
  Clock,
  CreditCard,
  Files,
  FileX,
  Landmark,
  MessageCircleQuestion,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { AccountDropdown } from "@/components/account-dropdown";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { TableSkeleton } from "@/components/skeletons/table-skeleton";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Heading } from "@/components/ui/text";
import { queryClient } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/_onboard/onboard/")({
  beforeLoad: async ({ context: { queryClient, trpc } }) => {
    const session = await queryClient.ensureQueryData(
      trpc.auth.session.queryOptions(),
    );

    if (session.org?.status === "approved") {
      throw redirect({
        to: "/onboard/approved",
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const {
    data: { org },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const navigate = Route.useNavigate();

  if (org?.status === "approved") return navigate({ to: "/onboard/approved" });

  if (org?.status === "pending") {
    return org.currentLimitCents ? (
      <Onboarding
        entryPoint="application"
        exitPoint="application"
        loadingComponent={
          <div className="w-full px-6 pt-12 sm:px-10">
            <TableSkeleton rows={18} />
          </div>
        }
        onDone={() => {
          queryClient.invalidateQueries(trpc.auth.session.queryOptions());
        }}
        onError={(error) => {
          queryClient.invalidateQueries(trpc.auth.session.queryOptions());
          toast.error("Error", {
            description: error.message,
          });
        }}
        className="h-full min-h-180"
      />
    ) : (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex size-14 items-center justify-center rounded-full border border-amber-200 bg-amber-100">
          <Clock className="size-6 text-amber-600" />
        </div>

        <h1 className="mt-6 font-semibold text-2xl">Account Pending</h1>
        <p className="mt-2 max-w-124 text-center text-muted-foreground text-sm">
          Thank you for completing the onboarding. We'll notify you via email
          once your account is approved. This typically takes 1-2 business days.
        </p>

        <ViewConnectedAccounts />
        <SupportActions />
      </div>
    );
  }

  if (org?.status === "waiting") {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex size-14 items-center justify-center rounded-full border border-amber-200 bg-amber-100">
          <FileX className="size-6 text-amber-600" />
        </div>

        <h1 className="mt-6 font-semibold text-2xl">More Info Required</h1>
        <p className="mt-2 max-w-prose text-center text-muted-foreground text-sm">
          Our team needs some additional information to approve a credit line.
          This may be because your organization has insufficient bank balance or
          account history. If you have additional bank accounts, connect them
          below to get a quicker approval.
        </p>

        <ViewConnectedAccounts />

        <SupportActions />
      </div>
    );
  }

  if (org?.status === "ineligible") {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex size-14 items-center justify-center rounded-full border border-red-200 bg-red-100">
          <X className="size-6 text-red-600" />
        </div>

        <h1 className="mt-6 font-semibold text-2xl">Account Ineligible</h1>
        <p className="mt-2 max-w-prose text-center text-muted-foreground text-sm">
          Based on the information you provided, your account is ineligible for
          Givefront Cards. We can only serve nonprofits in the United States,
          excluding those in North Dakota.
        </p>

        <ViewConnectedAccounts />

        <SupportActions />
      </div>
    );
  }

  if (org?.status === "closed") {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex size-14 items-center justify-center rounded-full border border-red-200 bg-red-100">
          <X className="size-6 text-red-600" />
        </div>

        <h1 className="mt-6 font-semibold text-2xl">Account Closed</h1>
        <p className="mt-2 max-w-prose text-center text-muted-foreground text-sm">
          We close accounts in the event of a default, fraud, or abuse. <br />{" "}
          You can view and export your Givefront Card statements below.
        </p>

        <ViewStatements />

        <SupportActions />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl py-2 sm:py-10">
      <div className="flex flex-col items-center gap-4 sm:gap-8 xl:flex-row xl:items-stretch">
        {/* Info cards - appear above on mobile, on right on desktop */}
        <div className="order-1 w-full max-w-[800px] shrink-0 grow px-6 sm:min-w-[400px] sm:px-10 xl:order-2 xl:w-[400px]">
          <div className="flex flex-col gap-1 rounded-lg border border-neutral-200 px-6 py-4">
            <h2 className="flex items-center gap-2 font-semibold text-neutral-800">
              <Clock className="size-4 text-neutral-600" />
              Apply in minutes
            </h2>
            <p className="text-neutral-600 text-sm">
              Issue corporate cards, build budgets, track receipts, and automate
              compliance.
            </p>

            <div className="mt-4 flex items-center gap-2 font-medium text-neutral-500 text-sm">
              What you'll need
              <div className="h-px grow bg-neutral-200" />
            </div>

            <div className="mt-2 flex flex-col">
              <div className="space-y-3">
                <div className="flex gap-3">
                  <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col">
                    <p className="font-medium">Personal Information</p>
                    <p className="text-muted-foreground text-sm">
                      We verify your identity to prevent fraud and abuse.{" "}
                      <strong>We do not run a personal credit check.</strong>
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Building className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col">
                    <p className="font-medium">Nonprofit Information</p>
                    <p className="text-muted-foreground text-sm">
                      Basic details about your organization.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col">
                    <p className="font-medium">Bank Account Connection</p>

                    <p className="text-muted-foreground text-sm">
                      We use Plaid, a secure third-party provider, to safely
                      link your accounts for payment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 p-3">
              <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <p className="text-green-800 text-sm">
                If your organization has multiple bank accounts, connect all of
                them to get the highest possible credit limit.
              </p>
            </div>
          </div>

          <div className="mt-2 mb-6 flex items-center justify-between">
            <Button
              // @ts-ignore
              onClick={() => window.Pylon("show")}
              size="sm"
              variant="outline"
              className=""
            >
              <MessageCircleQuestion />
              Chat With Support
            </Button>

            <AccountDropdown className="max-w-64 bg-white p-2" />
          </div>
        </div>

        {/* Main onboarding form - appears below info on mobile, on left on desktop */}
        <div className="order-2 flex h-full w-full min-w-0 max-w-[800px] shrink-0 flex-col items-center xl:order-1">
          <div className="w-full px-6 sm:px-10">
            <Heading className="relative z-50">Complete Onboarding</Heading>
          </div>
          <div
            className="-mt-10 w-full shrink-0"
            style={{
              clipPath: "polygon(0 2rem, 100% 2rem, 100% 100%, 0% 100%)",
            }}
          >
            <Onboarding
              entryPoint="application"
              exitPoint="application"
              loadingComponent={
                <div className="w-full px-6 pt-12 sm:px-10">
                  <TableSkeleton rows={18} />
                </div>
              }
              onDone={() => {
                queryClient.invalidateQueries(trpc.auth.session.queryOptions());
                toast.success("Onboarding Complete!");
              }}
              onError={(error) => {
                queryClient.invalidateQueries(trpc.auth.session.queryOptions());
                toast.error("Error", {
                  description: error.message,
                });
              }}
              className="h-full min-h-180"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewConnectedAccounts() {
  return (
    <Sheet>
      <SheetContent className="px-4 pt-16">
        <Onboarding
          entryPoint="application"
          exitPoint="application"
          loadingComponent={<SheetSkeleton />}
          onDone={() => {}}
          onError={() => {}}
          className="h-full"
        />
      </SheetContent>

      <SheetTrigger>
        <Button className="mt-8">
          <Landmark />
          View Connected Accounts
        </Button>
      </SheetTrigger>
    </Sheet>
  );
}

function ViewStatements() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="px-4 pt-16">
        <Statements
          onDone={() => setOpen(false)}
          loadingComponent={<SheetSkeleton />}
        />
      </SheetContent>

      <SheetTrigger>
        <Button className="mt-8">
          <Files />
          Access Statements
        </Button>
      </SheetTrigger>
    </Sheet>
  );
}

export function SupportActions({ className }: { className?: string }) {
  return (
    <div className={twMerge(className, "mt-8 flex items-center gap-4")}>
      <AccountDropdown className="rounded-md! border border-neutral-300 bg-white px-4! py-1!" />

      <Button
        // @ts-ignore
        onClick={() => window.Pylon("show")}
        variant="outline"
        className="h-11 w-48"
      >
        <MessageCircleQuestion />
        Chat With Support
      </Button>
    </div>
  );
}
